import React, { useState, useEffect } from 'react';
import { 
  Users, 
  MessageSquare, 
  History, 
  X, 
  Save,
  Clock,
  User,
  Search,
  Filter,
  Briefcase,
  Mail,
  Phone,
  Building,
  FileText,
  CheckCircle2,
  XCircle,
  AlertCircle,
  MapPin,
  Shield,
  Code,
  ChevronDown,
  ChevronRight,
  Sparkles,
  TrendingUp,
  Download,
  Plus,
  Calendar,
  Award,
  Globe,
  RefreshCw
} from 'lucide-react';

const InterviewPanel = ({ currentUser, API_BASE_URL }) => {
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [commentHistory, setCommentHistory] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Advanced Filter States
const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
const [companyFilter, setCompanyFilter] = useState([]);
const [positionFilter, setPositionFilter] = useState([]);
const [locationFilter, setLocationFilter] = useState([]);
const [workAuthFilter, setWorkAuthFilter] = useState([]);
const [domainFilter, setDomainFilter] = useState([]);
const [skillsFilter, setSkillsFilter] = useState('');
const [contractTypeFilter, setContractTypeFilter] = useState([]);
  
  // Dropdown states for multi-select
const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
const [showPositionDropdown, setShowPositionDropdown] = useState(false);
const [showLocationDropdown, setShowLocationDropdown] = useState(false);
const [showWorkAuthDropdown, setShowWorkAuthDropdown] = useState(false);
const [showDomainDropdown, setShowDomainDropdown] = useState(false);
const [showContractTypeDropdown, setShowContractTypeDropdown] = useState(false);

  // Get unique values for filters
const uniqueCompanies = [...new Set(candidates.map(c => c.company).filter(Boolean))];
const uniquePositions = [...new Set(candidates.map(c => c.position).filter(Boolean))];
const uniqueLocations = [...new Set(candidates.map(c => `${c.projectCity}, ${c.projectState}`).filter(c => c !== ', '))];
const uniqueWorkAuth = [...new Set(candidates.map(c => c.workAuthorization).filter(Boolean))];
const uniqueDomains = [...new Set(candidates.map(c => c.domain).filter(Boolean))];
const uniqueContractTypes = [...new Set(candidates.map(c => c.contractorStatus).filter(Boolean))];

  useEffect(() => {
    fetchInterviewCandidates();
  }, []);

  useEffect(() => {
    let filtered = candidates;

    if (searchTerm) {
      filtered = filtered.filter(candidate =>
        candidate.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.position?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        candidate.primarySkills?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(candidate => candidate.interview_status === statusFilter);
    }

    // Advanced Filters
if (companyFilter.length > 0) {
  filtered = filtered.filter(c => companyFilter.includes(c.company));
}
if (positionFilter.length > 0) {
  filtered = filtered.filter(c => positionFilter.includes(c.position));
}
if (locationFilter.length > 0) {
  filtered = filtered.filter(c => locationFilter.includes(`${c.projectCity}, ${c.projectState}`));
}
if (workAuthFilter.length > 0) {
  filtered = filtered.filter(c => workAuthFilter.includes(c.workAuthorization));
}
if (domainFilter.length > 0) {
  filtered = filtered.filter(c => domainFilter.includes(c.domain));
}
if (skillsFilter) {
  filtered = filtered.filter(c => 
    c.primarySkills?.toLowerCase().includes(skillsFilter.toLowerCase()) ||
    c.secondarySkills?.toLowerCase().includes(skillsFilter.toLowerCase())
  );
}
if (contractTypeFilter.length > 0) {
  filtered = filtered.filter(c => contractTypeFilter.includes(c.contractorStatus));
}

    setFilteredCandidates(filtered);
  }, [searchTerm, statusFilter, candidates, companyFilter, positionFilter, locationFilter, workAuthFilter, domainFilter]);

  const fetchInterviewCandidates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/interview-panel-api.php?action=candidates`);
      const data = await response.json();
      
      if (data.success) {
        setCandidates(data.candidates || []);
        setFilteredCandidates(data.candidates || []);
      } else {
        console.error('Error:', data.error);
        alert(data.error || 'Failed to fetch candidates');
      }
    } catch (error) {
      console.error('Error fetching interview candidates:', error);
      alert('Failed to fetch interview candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommentHistory = async (candidateId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/interview-panel-api.php?action=comments&candidate_id=${candidateId}`);
      const data = await response.json();
      
      if (data.success) {
        setCommentHistory(data.comments || []);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedCandidate) return;

    try {
      const response = await fetch(`${API_BASE_URL}/interview-panel-api.php?action=add_comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_id: selectedCandidate.id,
          comment: newComment,
          user_id: currentUser.id,
          user_name: `${currentUser.first_name} ${currentUser.last_name}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setNewComment('');
        setShowCommentModal(false);
        fetchInterviewCandidates();
        alert('Comment added successfully!');
      } else {
        alert(data.error || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleViewHistory = (candidate) => {
    setSelectedCandidate(candidate);
    fetchCommentHistory(candidate.id);
    setShowHistoryModal(true);
  };

  const handleStatusChange = async (candidateId, newStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/interview-panel-api.php?action=update_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidateId: candidateId,
          status: newStatus
        })
      });

      const data = await response.json();
      
      if (data.success) {
        fetchInterviewCandidates();
        alert('Status updated successfully!');
      } else {
        alert(data.error || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const toggleRowExpansion = (candidateId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(candidateId)) {
      newExpanded.delete(candidateId);
    } else {
      newExpanded.add(candidateId);
    }
    setExpandedRows(newExpanded);
  };

  const toggleFilter = (filterArray, setFilter, value) => {
    if (filterArray.includes(value)) {
      setFilter(filterArray.filter(item => item !== value));
    } else {
      setFilter([...filterArray, value]);
    }
  };

 const clearAllFilters = () => {
  setSearchTerm('');
  setStatusFilter('all');
  setCompanyFilter([]);
  setPositionFilter([]);
  setLocationFilter([]);
  setWorkAuthFilter([]);
  setDomainFilter([]);
  setSkillsFilter('');
  setContractTypeFilter([]);
};

  const MultiSelectDropdown = ({ label, options, selectedValues, onToggle, showDropdown, setShowDropdown }) => {
    const displayText = selectedValues.length === 0 
      ? 'All' 
      : selectedValues.length === 1 
        ? selectedValues[0]
        : `${selectedValues.length} selected`;

    return (
      <div className="relative" style={{ zIndex: showDropdown ? 100 : 'auto' }}>
        <label className="text-xs font-semibold text-slate-700 block mb-1.5">{label}</label>
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-xs bg-white hover:border-slate-300 transition-colors text-left flex items-center justify-between"
        >
          <span className={selectedValues.length === 0 ? 'text-slate-400' : 'text-slate-900'}>
            {displayText}
          </span>
          <ChevronDown className={`w-3 h-3 ml-1 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
            <div className="absolute z-50 mt-1 left-0 right-0 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl">
              <div className="p-2">
                {options.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-500 text-center">No options</div>
                ) : (
                  options.map((option) => (
                    <label key={option} className="flex items-center px-3 py-2 hover:bg-slate-50 rounded-md cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(option)}
                        onChange={() => onToggle(option)}
                        className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-2 focus:ring-indigo-500"
                      />
                      <span className="ml-2 text-xs text-slate-700">{option}</span>
                    </label>
                  ))
                )}
              </div>
              {selectedValues.length > 0 && (
                <div className="border-t border-slate-200 p-2">
                  <button
                    onClick={() => {
                      onToggle(null, true);
                      setShowDropdown(false);
                    }}
                    className="w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const StatusBadge = ({ status }) => {
    const configs = {
      available: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle2, dot: 'bg-emerald-500' },
      'not available': { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: XCircle, dot: 'bg-rose-500' },
      'na': { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200', icon: AlertCircle, dot: 'bg-slate-400' },
      'no response': { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock, dot: 'bg-amber-500' },
      'out of vdart': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', icon: XCircle, dot: 'bg-purple-500' }
    };

    const config = configs[status?.toLowerCase()] || configs.na;
    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.bg} ${config.text} ${config.border} shadow-sm`}>
        <div className={`w-2 h-2 rounded-full ${config.dot} animate-pulse`}></div>
        <Icon className="w-3.5 h-3.5" />
        <span className="text-xs font-semibold capitalize tracking-wide">{status || 'Pending'}</span>
      </div>
    );
  };

  const Avatar = ({ name }) => {
    const initials = name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
    const gradients = [
      'from-blue-500 via-indigo-500 to-purple-600',
      'from-emerald-500 via-teal-500 to-cyan-600',
      'from-orange-500 via-red-500 to-pink-600',
      'from-violet-500 via-purple-500 to-fuchsia-600'
    ];
    const index = name ? name.length % gradients.length : 0;

    return (
      <div className={`w-11 h-11 bg-gradient-to-br ${gradients[index]} rounded-xl flex items-center justify-center shadow-lg text-white font-bold text-sm ring-2 ring-white`}>
        {initials}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-700 font-semibold">Loading Interview Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Decorative Elements */}
      <div className="fixed top-0 left-0 w-full h-96 bg-gradient-to-b from-indigo-100/40 to-transparent pointer-events-none"></div>
      <div className="fixed top-20 right-20 w-72 h-72 bg-purple-200/20 rounded-full blur-3xl pointer-events-none"></div>
      <div className="fixed bottom-20 left-20 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 backdrop-blur-sm border border-indigo-200 text-indigo-600 text-xs font-bold uppercase tracking-wider mb-3 shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
                Talent Acquisition
              </div>
              <h1 className="text-4xl font-black text-slate-900 tracking-tight mb-2">
                Interview <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Pipeline</span>
              </h1>
              <p className="text-slate-600 text-lg">Streamline your candidate evaluation and tracking</p>
            </div>

            {/* Stats Cards */}
            <div className="flex gap-4">
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/50 p-5 min-w-[160px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Active</p>
                    <p className="text-2xl font-black text-slate-900">{filteredCandidates.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/50 p-5 min-w-[160px]">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Available</p>
                    <p className="text-2xl font-black text-slate-900">
                      {candidates.filter(c => c.interview_status === 'available').length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/50 p-2">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-indigo-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, skills, or company..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 bg-slate-50/50 border-none rounded-xl focus:ring-2 focus:ring-indigo-500/30 focus:bg-white text-slate-800 placeholder-slate-400 font-medium transition-all outline-none"
                />
              </div>

              <div className="relative w-64">
                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full pl-11 pr-10 py-3.5 bg-slate-50/50 border-none rounded-xl focus:ring-2 focus:ring-indigo-500/30 text-slate-700 font-semibold cursor-pointer appearance-none transition-all outline-none"
                >
                  <option value="all">All Statuses</option>
                  <option value="available">Available</option>
                  <option value="not available">Not Available</option>
                  <option value="na">N/A</option>
                  <option value="no response">No Response</option>
                  <option value="out of vdart">Out of VDart</option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>

              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm transition-all ${
                  showAdvancedFilters 
                    ? 'bg-indigo-600 text-white shadow-lg' 
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Advanced
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </button>

              <button 
                onClick={fetchInterviewCandidates}
                className="p-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

         {/* Advanced Filters Panel */}
{showAdvancedFilters && (
  <div className="mt-4 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/50 p-5">
    <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
      <Filter className="w-4 h-4 text-indigo-600" />
      Advanced Filters
    </h3>
    
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
      <MultiSelectDropdown
        label="Company"
        options={uniqueCompanies}
        selectedValues={companyFilter}
        onToggle={(value, clearAll) => {
          if (clearAll) setCompanyFilter([]);
          else toggleFilter(companyFilter, setCompanyFilter, value);
        }}
        showDropdown={showCompanyDropdown}
        setShowDropdown={setShowCompanyDropdown}
      />

      <MultiSelectDropdown
        label="Position"
        options={uniquePositions}
        selectedValues={positionFilter}
        onToggle={(value, clearAll) => {
          if (clearAll) setPositionFilter([]);
          else toggleFilter(positionFilter, setPositionFilter, value);
        }}
        showDropdown={showPositionDropdown}
        setShowDropdown={setShowPositionDropdown}
      />

      <MultiSelectDropdown
        label="Location"
        options={uniqueLocations}
        selectedValues={locationFilter}
        onToggle={(value, clearAll) => {
          if (clearAll) setLocationFilter([]);
          else toggleFilter(locationFilter, setLocationFilter, value);
        }}
        showDropdown={showLocationDropdown}
        setShowDropdown={setShowLocationDropdown}
      />

      <MultiSelectDropdown
        label="Work Authorization"
        options={uniqueWorkAuth}
        selectedValues={workAuthFilter}
        onToggle={(value, clearAll) => {
          if (clearAll) setWorkAuthFilter([]);
          else toggleFilter(workAuthFilter, setWorkAuthFilter, value);
        }}
        showDropdown={showWorkAuthDropdown}
        setShowDropdown={setShowWorkAuthDropdown}
      />

      <MultiSelectDropdown
        label="Domain"
        options={uniqueDomains}
        selectedValues={domainFilter}
        onToggle={(value, clearAll) => {
          if (clearAll) setDomainFilter([]);
          else toggleFilter(domainFilter, setDomainFilter, value);
        }}
        showDropdown={showDomainDropdown}
        setShowDropdown={setShowDomainDropdown}
      />

      <MultiSelectDropdown
        label="Contract Type"
        options={uniqueContractTypes}
        selectedValues={contractTypeFilter}
        onToggle={(value, clearAll) => {
          if (clearAll) setContractTypeFilter([]);
          else toggleFilter(contractTypeFilter, setContractTypeFilter, value);
        }}
        showDropdown={showContractTypeDropdown}
        setShowDropdown={setShowContractTypeDropdown}
      />

      {/* Skills Search Input */}
      <div className="relative">
        <label className="text-xs font-semibold text-slate-700 block mb-1.5">Skills</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search skills..."
            value={skillsFilter}
            onChange={(e) => setSkillsFilter(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-xs bg-white hover:border-slate-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all outline-none"
          />
          {skillsFilter && (
            <button
              onClick={() => setSkillsFilter('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-100 rounded"
            >
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          )}
        </div>
      </div>
    </div>

              <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
                <span className="text-sm text-slate-600">
                  <span className="font-bold text-indigo-600">{filteredCandidates.length}</span> candidates match your filters
                </span>
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg font-semibold transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Candidates List */}
        <div className="space-y-4">
          {filteredCandidates.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 p-20 text-center shadow-lg">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-slate-300" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">No Candidates Found</h3>
              <p className="text-slate-500 max-w-md mx-auto mb-6">
                We couldn't find any candidates matching your search criteria.
              </p>
              <button 
                onClick={clearAllFilters}
                className="px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-lg"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            filteredCandidates.map((candidate) => {
              const isExpanded = expandedRows.has(candidate.id);
              
              return (
                <div 
                  key={candidate.id}
                  className={`group bg-white/80 backdrop-blur-xl rounded-2xl border transition-all duration-300 overflow-hidden ${
                    isExpanded 
                      ? 'border-indigo-300 shadow-2xl shadow-indigo-500/10 scale-[1.01]' 
                      : 'border-slate-200/50 shadow-lg hover:shadow-xl hover:border-indigo-200 hover:-translate-y-0.5'
                  }`}
                >
                  {/* Main Row */}
                  <div 
                    className="p-6 cursor-pointer"
                    onClick={() => toggleRowExpansion(candidate.id)}
                  >
                    <div className="flex items-center gap-6">
                      {/* Avatar & Basic Info */}
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <Avatar name={candidate.name} />
                        <div className="min-w-0">
                          <h3 className="font-bold text-slate-900 text-lg truncate group-hover:text-indigo-600 transition-colors">
                            {candidate.name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{candidate.email}</span>
                          </div>
                        </div>
                      </div>

                      {/* Company Info */}
                      <div className="hidden md:block min-w-[200px]">
                        <div className="flex items-center gap-2 text-slate-800 font-semibold mb-1">
                          <Building className="w-4 h-4 text-indigo-500" />
                          <span className="truncate">{candidate.company}</span>
                        </div>
                        <p className="text-sm text-slate-600 truncate pl-6">{candidate.position}</p>
                      </div>

                      {/* Skills Preview */}
                      <div className="hidden lg:flex gap-2 min-w-[200px]">
                        {candidate.primarySkills?.split(',').slice(0, 2).map((skill, i) => (
                          <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-lg border border-indigo-100">
                            {skill.trim()}
                          </span>
                        ))}
                        {candidate.primarySkills?.split(',').length > 2 && (
                          <span className="text-xs text-slate-400 py-1 font-medium">+{candidate.primarySkills.split(',').length - 2}</span>
                        )}
                      </div>

                      {/* Status & Actions */}
                      <div className="flex items-center gap-3">
                        <StatusBadge status={candidate.interview_status} />
                        
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCandidate(candidate);
                              setShowCommentModal(true);
                            }}
                            className="p-2.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                            title="Add Comment"
                          >
                            <MessageSquare className="w-5 h-5" />
                          </button>
                          
                          <div className={`p-2 rounded-xl transition-all duration-300 ${isExpanded ? 'bg-indigo-50 rotate-180' : 'hover:bg-slate-100'}`}>
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-100 bg-slate-50/50 p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        
                        {/* Action Bar */}
                        <div className="md:col-span-3 bg-white rounded-2xl p-5 border border-indigo-100 shadow-sm">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2.5 bg-indigo-100 text-indigo-600 rounded-xl">
                                <Shield className="w-5 h-5" />
                              </div>
                              <div>
                                <h4 className="font-bold text-slate-800 text-sm">Update Interview Status</h4>
                                <p className="text-xs text-slate-500">Change candidate pipeline stage</p>
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                              <select
                                value={candidate.interview_status || ''}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleStatusChange(candidate.id, e.target.value);
                                }}
                                className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 outline-none transition-all cursor-pointer"
                              >
                                <option value="">Select Status...</option>
                                <option value="available">Available</option>
                                <option value="not available">Not Available</option>
                                <option value="na">NA</option>
                                <option value="no response">No Response</option>
                                <option value="out of vdart">Out of VDart</option>
                              </select>
                              
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewHistory(candidate);
                                }}
                                className="px-4 py-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 hover:text-indigo-600 hover:border-indigo-200 transition-all text-sm flex items-center gap-2"
                              >
                                <History className="w-4 h-4" />
                                History
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Contact Info Card */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                          <h4 className="font-bold text-slate-800 mb-4 flex items-center text-sm uppercase tracking-wider">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg mr-2">
                              <Mail className="w-4 h-4" />
                            </div>
                            Contact Details
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Email</span>
                              <span className="text-sm text-slate-800 font-semibold text-right">{candidate.email}</span>
                            </div>
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Phone</span>
                              <span className="text-sm text-slate-800 font-semibold">{candidate.phone || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Location</span>
                              <span className="text-sm text-slate-800 font-semibold">{candidate.projectCity}, {candidate.projectState}</span>
                            </div>
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Work Auth</span>
                              <span className="text-sm text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded">{candidate.workAuthorization}</span>
                            </div>
                          </div>
                        </div>

                        {/* Professional Info Card */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                          <h4 className="font-bold text-slate-800 mb-4 flex items-center text-sm uppercase tracking-wider">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg mr-2">
                              <Briefcase className="w-4 h-4" />
                            </div>
                            Professional Profile
                          </h4>
                          <div className="space-y-3">
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Company</span>
                              <span className="text-sm text-slate-800 font-semibold">{candidate.company}</span>
                            </div>
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Position</span>
                              <span className="text-sm text-indigo-600 font-semibold bg-indigo-50 px-2 py-0.5 rounded text-right">{candidate.position}</span>
                            </div>
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Domain</span>
                              <span className="text-sm text-slate-800 font-semibold">{candidate.domain}</span>
                            </div>
                            <div className="flex justify-between items-start py-2 group">
                              <span className="text-slate-500 text-sm font-medium">Entity</span>
                              <span className="text-sm text-slate-800 font-semibold">{candidate.businessEntity}</span>
                            </div>
                          </div>
                        </div>

                        {/* Skills Card */}
                        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
                          <h4 className="font-bold text-slate-800 mb-4 flex items-center text-sm uppercase tracking-wider">
                            <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg mr-2">
                              <Code className="w-4 h-4" />
                            </div>
                            Technical Skills
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Primary</p>
                              <div className="flex flex-wrap gap-1.5">
                                {candidate.primarySkills?.split(',').map((skill, i) => (
                                  <span key={i} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-100">
                                    {skill.trim()}
                                  </span>
                                ))}
                              </div>
                            </div>
                            {candidate.secondarySkills && (
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Secondary</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {candidate.secondarySkills.split(',').map((skill, i) => (
                                    <span key={i} className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg border border-slate-200">
                                      {skill.trim()}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Latest Comment */}
                        {candidate.latest_comment && (
                          <div className="md:col-span-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-200">
                            <div className="flex gap-4">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                                  <MessageSquare className="w-5 h-5 text-amber-600" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <h5 className="text-sm font-bold text-amber-900 uppercase tracking-wide mb-2">Latest Feedback</h5>
                                <p className="text-slate-800 text-sm leading-relaxed mb-3">"{candidate.latest_comment}"</p>
                                <div className="flex items-center gap-3 text-xs text-amber-800/70 font-medium">
                                  <span className="flex items-center gap-1.5">
                                    <User className="w-3.5 h-3.5" />
                                    {candidate.comment_by}
                                  </span>
                                  <span className="w-1 h-1 bg-amber-300 rounded-full"></span>
                                  <span className="flex items-center gap-1.5">
                                    <Clock className="w-3.5 h-3.5" />
                                    {new Date(candidate.comment_date).toLocaleDateString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Add Comment Modal */}
        {showCommentModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowCommentModal(false)} />
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl z-10 overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Add New Comment</h2>
                      {selectedCandidate && (
                        <p className="text-indigo-100 text-sm mt-0.5">For: {selectedCandidate.name}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCommentModal(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Enter your feedback or observations..."
                  rows="6"
                  className="w-full p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all outline-none resize-none text-sm font-medium placeholder-slate-400"
                  autoFocus
                />
              </div>

              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowCommentModal(false)}
                  className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:text-slate-800 hover:bg-slate-200/50 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-xl shadow-lg shadow-indigo-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Save Comment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal - Fixed with proper spacing */}
{showHistoryModal && (
  <div className="fixed inset-0 z-50 flex items-start justify-center pt-24 pb-24 p-4">
    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowHistoryModal(false)} />
    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[calc(100vh-12rem)] flex flex-col z-10 overflow-hidden">
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                      <History className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">Activity Timeline</h2>
                      {selectedCandidate && (
                        <p className="text-purple-100 text-sm mt-0.5">For: {selectedCandidate.name}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 text-white hover:bg-white/30 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
                <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-200">
                  {commentHistory.length > 0 ? (
                    commentHistory.map((item, index) => (
                      <div key={index} className="relative pl-12 group">
                        <div className="absolute left-[11px] top-4 w-4 h-4 bg-white border-4 border-indigo-500 rounded-full z-10 group-hover:scale-125 transition-transform shadow-sm" />
                        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all">
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              <div className="px-3 py-1 bg-indigo-50 rounded-lg text-xs font-bold text-indigo-700 uppercase tracking-wide">
                                {item.user_name?.split(' ')[0]}
                              </div>
                              <span className="text-xs text-slate-400 font-semibold">
                                {new Date(item.created_at).toLocaleString()}
                              </span>
                            </div>
                          </div>
                          <p className="text-slate-700 text-sm font-medium leading-relaxed">{item.comment}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-16">
                      <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500 font-medium">No activity recorded yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewPanel;